import React, { useContext, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon } from '@node-space/ui-components'
import { useWalletContext } from 'hooks/context'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface WalletButtonsFiatProps {
  disabled?: boolean
}

export const WalletButtonsFiat = ({ disabled }: WalletButtonsFiatProps) => {
  const { t } = useTranslation()
  const { enableUsLaunchRestrictions } = useFeatureFlags()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const wallet = useWalletContext()
  const [, setWalletAction] = useContext(WalletActionContext)
  const customerModel = useAtomValue(customerModelAtom)

  // Unified Payouts
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const isSelectedWalletActive = useIsActiveWallet(wallet)

  const handleOpenMoveSheet = () => {
    setWalletAction({
      actionId: ActionIds.MOVE,
      header: t('customers.moveFunds'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWalletType: WalletTransferType.MAIN,
        sourceWallet: wallet,
        customerModel,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenConvertSheet = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_CONVERT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
      isFiat: true,
    })
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenFiatDepositSheet = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_DEPOSIT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
    })
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        sourceWalletId: wallet?.id,
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenSendFiatSheet = () => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: wallet?.lsid,
      })

      setIsSheetOpen(true)
    }
  }

  return (
    isSelectedWalletActive && (
      <Box flex padding={8} className="gap-3 sm:pt-0">
        {!enableUsLaunchRestrictions && (
          <Button
            testId="buttonConvert"
            disabled={disabled}
            onClick={handleOpenConvertSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('convert')}
          </Button>
        )}

        {(wallet?.supportsWithdrawals || wallet?.supportsThirdParty) && (
          <Button
            testId="sendFiat"
            disabled={disabled}
            onClick={handleOpenSendFiatSheet}
            iconElement={<Icon name="CoinArrowOutCustom" />}
          >
            {t('wallets.fiatSend.sendFiat')}
          </Button>
        )}
        {wallet?.supportsDeposits && (
          <Button
            testId="buttonDeposit"
            disabled={disabled}
            onClick={handleOpenFiatDepositSheet}
            iconElement={<Icon name="CoinArrowInCustom" />}
          >
            {t('deposit')}
          </Button>
        )}
        {wallet?.supportsInternalTransfers && (
          <Button
            testId="buttonMove"
            disabled={disabled}
            onClick={handleOpenMoveSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('customers.move')}
          </Button>
        )}
        <WalletSheet isSheetOpen={isSheetOpen} />
      </Box>
    )
  )
}
